import * as yup from 'yup';

import {
  adminPagePermissions,
  eventReviewPagePermissions,
  globalPermissions,
  mobilePermissions,
  type PermissionWithLabel,
  roomsPagePermissions,
  zuiPermissions,
} from './permissions';

import { useIsAdmin } from '../../../../../hooks/useIsAdmin';
import TrainingVideoCell from '../components/TrainingVideoCell';
import { buildPermissionDataField } from '../helpers/buildPermissionDataField';

import type { DataFields } from '../../../types/DataFields';
import type { RoleFieldTypes } from '../types/RoleFieldTypes';

const numberSchema = yup
  .number()
  .typeError('This setting must be a number')
  .min(0)
  .integer('This setting must be a whole number')
  .transform((value, originalValue) => (originalValue === '' ? null : value))
  .nullable();

const getAccesibleRoles = (
  permissionsWithLabel: PermissionWithLabel[],
  isAdmin?: boolean,
) =>
  permissionsWithLabel.filter((e) => (isAdmin ? true : !e.inspirenAdminOnly));

const getOrgProfileFields = (isAdmin: boolean): DataFields<RoleFieldTypes> => [
  {
    field: 'displayName',
    label: 'Role Name',
    width: 150,
    editType: 'text',
    editable: true,
    schema: yup.string().required('Display Name is required'),
  },
  {
    field: 'templateName',
    label: 'Template',
    width: 150,
    editType: 'text',
    editable: false,
  },
  {
    field: 'maxAssign',
    label: 'Max Assignments',
    width: 150,
    editType: 'text',
    editable: true,
    schema: numberSchema,
  },
  {
    field: 'isLicensed',
    label: 'Licensed',
    width: 80,
    type: 'boolean',
    editType: 'boolean',
    editable: true,
    initialValue: false,
  },
  {
    field: 'requireTrainingVideoMobile',
    label: 'Require Training Video (Mobile)?',
    width: 250,
    type: 'boolean',
    editType: 'boolean',
    editable: true,
    initialValue: false,
  },
  {
    field: 'trainingVideoUrlMobile',
    label: 'Training Video (Mobile)',
    width: 200,
    editable: true,
    renderCell: ({ value }) => <TrainingVideoCell value={value} />,
  },
  {
    field: 'showInStaffView',
    label: 'Show In Staff View',
    width: 160,
    type: 'boolean',
    editType: 'boolean',
    editable: true,
    initialValue: true,
  },
  {
    field: 'fallVideoSMS',
    label: 'Fall Video SMS',
    width: 160,
    type: 'boolean',
    editType: 'boolean',
    editable: true,
    initialValue: false,
  },

  ...getAccesibleRoles(globalPermissions, isAdmin).map((permission) =>
    buildPermissionDataField(permission.id, permission.label),
  ),

  buildPermissionDataField('virtual-care.access', 'Virtual Care Access'),

  buildPermissionDataField(
    'virtual-care.analytics.view',
    'View Analytics Page',
  ),

  buildPermissionDataField(
    'virtual-care.tableau.view',
    'View Tableau Launch Button',
  ),

  buildPermissionDataField('virtual-care.rooms.view', 'View Rooms Page'),

  ...getAccesibleRoles(roomsPagePermissions, isAdmin).map((permission) =>
    buildPermissionDataField(permission.id, permission.label),
  ),

  buildPermissionDataField(
    'virtual-care.event-review.view',
    'View Event Review Page',
  ),

  ...getAccesibleRoles(eventReviewPagePermissions, isAdmin).map((permission) =>
    buildPermissionDataField(permission.id, permission.label),
  ),

  buildPermissionDataField('virtual-care.admin.view', 'View Admin Page'),

  ...getAccesibleRoles(adminPagePermissions, isAdmin).map((permission) =>
    buildPermissionDataField(permission.id, permission.label),
  ),

  buildPermissionDataField('mobile.access', 'Mobile Access'),
  ...getAccesibleRoles(mobilePermissions, isAdmin).map((permission) =>
    buildPermissionDataField(permission.id, permission.label),
  ),

  ...getAccesibleRoles(zuiPermissions, isAdmin).map((permission) =>
    buildPermissionDataField(permission.id, permission.label),
  ),
];

export const useOrgProfileFields = () => {
  const { isAdmin } = useIsAdmin();

  return getOrgProfileFields(isAdmin);
};
